/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-nested-ternary */
import { useMutation } from '@apollo/client';
import { Col, Row, Tooltip } from 'antd';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import AvatarGroup from 'components/common/AvatarGroup';
import DefaultModal from 'components/common/DefaultModal';
import IconComponent from 'components/common/IconComponent';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import { FORMAT_DATE, FORMAT_DATE_TIME } from 'constants/days';
import { FormikProps } from 'formik';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { useSingleDataQuery } from 'hooks/useFastGqlQuery';
import useRoleCheck from 'hooks/useRoleCheck';
import useUploadTransactionAppointmentFile from 'hooks/useUploadTransactionAppointmentFile';
import { flatten, isEmpty } from 'lodash';
import moment from 'moment';
import React, { RefAttributes, RefObject, useEffect, useMemo, useState } from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import ClientAssignTable from 'routes/Transactions/components/ClientAssignTable';
import { CREATE_APPOINTMENT, DELETE_APPOINTMENT, UPDATE_APPOINTMENT } from 'services/graphql/mutations/appointments';
import { DELETE_FILE_SINGLE } from 'services/graphql/mutations/files';
import { UPDATE_TRANSACTION } from 'services/graphql/mutations/transactions';
import { IAppointment, IAppointmentState } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { DocumentSubType, IDocument, IFile } from 'types/file';
import { ILocation } from 'types/location';
import { ITransactionType } from 'types/services';
import { ITransaction, TransactionStatusEnum } from 'types/transaction';
import { RoleEnum } from 'types/user';
import {
  defaultNotification,
  fastGetDefaultAvatarUrl,
  formatCurrency,
  formatListNames,
  getFormattedDate,
  getUsStates,
} from 'utils';
import IntlMessages, { formatMessage } from 'utils/IntlMessages';

// import SweetAlert from 'react-bootstrap-sweetalert';
// import AuthService from 'services/api/auth.service';
// import { RoleEnum } from 'types/user';
import {
  InputsContainerStyled,
  TextOnlyButtonStyled,
  TransactionFormContainerStyled,
  TransactionSectionContainerStyled,
} from '../../styled';
import { INotaryAppointmentInputProps, ISignatoryInputProps } from '../../types';
import AppointmentList from './AppointmentList';
import AppointmentModal from './AppointmentModal';
import DocumentList from './DocumentList';
import DocumentModal from './DocumentModal';
import DocumentReturnLocationModal from './DocumentReturnLocationModal';
import NotaryTableModal from './NotaryTableModal';
import SectionHeader from './SectionHeader';
import SignatoryCard from './SignatoryCard';
import SignatoryModal from './SignatoryModal';
import TransactionModal from './TransactionModal';
import { ModalType } from './types';

const TransactionForm: React.FC<{
  data?: ITransaction;
  transactionTypesData?: ITransactionType[];
  refetchTransactionData?: GeneralFunction;
  formRef?: RefObject<FormikProps<Record<string, any>>> & RefAttributes<any>;
  hideSubmitButton?: boolean;
  callbackOnSuccess?: GeneralFunction;
  callbackOnFailure?: GeneralFunction;
  onSubmitCallback?: GeneralFunction;
  transactionTypeLoading?: boolean;
  userPermittedLocationsData?: ILocation[];
  fetchNextPageLocationData?: GeneralFunction;
}> = ({
  data: transactionData,
  transactionTypesData,
  refetchTransactionData,
  callbackOnFailure,
  callbackOnSuccess,
  userPermittedLocationsData,
  fetchNextPageLocationData,
}) => {
  const intl = useIntl();
  const [modalType, setModalType] = useState<ModalType | undefined>();
  const user = useCurrentLoggedInUser();
  const userRole = user.role;
  const transactionType =
    transactionTypesData?.find((tt) => tt.id === transactionData?.transactionType.id) ||
    transactionData?.transactionType;
  const isClient = userRole === RoleEnum.CLIENT;
  const isClientRelatedToTransaction =
    isClient &&
    (transactionData?.createdBy?.id === user?.id || transactionData?.assigners?.some((u) => u.id === user?.id));
  // Query service & transaction price

  const [signatoriesData, setSignatoriesData] = useState<Record<string, ISignatoryInputProps>>();
  const [notaryAppointments, setNotaryAppointments] = useState<Record<string, IAppointmentState>>();
  const [selectedSignatory, setSelectedSignatory] = useState<ISignatoryInputProps>();
  const [selectedAppointment, setSelectedAppointment] = useState<IAppointmentState>();
  const [selectedFile, setSelectedFile] = useState<any>();

  const [transactionAppointmentDocument, transactionScannedDocument, transactionShippingDocument] = useMemo(
    () =>
      transactionData?.documents?.reduce((acc, doc) => {
        let fileTypeIndex = 0;
        if (doc.subType === DocumentSubType.SHIPPING_LABEL) {
          fileTypeIndex = 2;
        }
        if (doc.subType === DocumentSubType.SCANNED) {
          fileTypeIndex = 1;
        }
        acc[fileTypeIndex] = doc;
        return acc;
      }, [] as IDocument[]) || [],
    [transactionData?.documents],
  );

  const isTransactionEditable = useMemo(() => {
    if (
      (transactionData &&
        (transactionData?.status === TransactionStatusEnum.CANCELLED ||
          transactionData?.status === TransactionStatusEnum.CLOSED)) ||
      transactionData?.status === TransactionStatusEnum.MATCHED
    ) {
      return false;
    }
    return true;
  }, [transactionData]);

  // const { priceRON, priceInPerson } = useMemo(() => {
  //   const tempPriceInPerson =
  //     transactionType?.userPrice?.priceInPerson || transactionType?.defaultPrice?.clientInPersonFee || 0;
  //   const tempPriceRON = transactionType?.userPrice?.priceRON || transactionType?.defaultPrice?.clientRONFee || 0;
  //   return { priceRON: tempPriceRON, priceInPerson: tempPriceInPerson };
  // }, [transactionType]);

  // const totalFee = useMemo(
  //   () =>
  //     transactionData?.appointment?.reduce((acc: number, appointments) => {
  //       if (appointments.type === AppointmentNotaryType.IN_PERSON) {
  //         return acc + priceInPerson;
  //       }
  //       return acc + priceRON;
  //     }, 0),
  //   [transactionData?.appointment],
  // );

  useEffect(() => {
    setSignatoriesData(
      transactionData?.signatories?.reduce((acc: Record<string, any>, signatory) => {
        acc[signatory.id] = signatory;
        return acc;
      }, {}) || {},
    );
    setNotaryAppointments(() =>
      transactionData && transactionData.appointment
        ? flatten([transactionData.appointment]).reduce((acc: Record<string, any>, appointment, index) => {
            const tweakedAppointment =
              transactionData.status === TransactionStatusEnum.CANCELLED
                ? {
                    ...appointment,
                    status: undefined,
                    id: undefined,
                  }
                : appointment;
            acc[tweakedAppointment?.id || index] = tweakedAppointment;
            return acc;
          }, {})
        : {},
    );
    if (selectedAppointment) {
      const newSelectedAppointment = transactionData?.appointment?.find((app) => app.id === selectedAppointment.id);
      if (newSelectedAppointment) setSelectedAppointment(newSelectedAppointment);
    }
  }, [transactionData, transactionTypesData]);

  const PaymentComponent = useMemo(
    () =>
      transactionData?.isPaid ? (
        <IconComponent name='check-circle-o' color='#81D742' />
      ) : (
        <IconComponent name='close-circle' color='red' />
      ),
    [transactionData],
  );

  // const [selectedNotary, setSelectedNotary] = useState(data?.notary?.id);
  // Transaction
  // ---  Form Field

  const transactionTextField: DataFieldProps[] = useMemo(() => {
    const totalFee = transactionData?.clientFee;
    const feeValue = `${formatCurrency(totalFee || 0)}`;
    const feeField = { label: 'Total Fee', value: feeValue };
    return transactionData
      ? [
          {
            label: 'Payment Status',
            value: PaymentComponent,
            sideFields: [{ ...feeField, size: 12 }],
          },
        ]
      : [feeField];
  }, [transactionData]);

  const onAssignTransactionClick = () => {
    setModalType('assign-transaction');
  };

  const transactionFormField: DataFieldProps[] = useMemo(() => {
    const assigners = transactionData?.assigners || [];
    return [
      { value: transactionData?.requiredScanDoc ? 'Yes' : 'No', label: 'Require Scanned Electronic Delivery' },
      {
        label: 'Description',
        value: transactionData?.description || '',
        sideFields: [{ label: 'Reference Number', value: transactionData?.reference || '', size: 12 }],
      },
      {
        label: 'Price Per Appointment',
        value:
          formatCurrency(
            isClient
              ? transactionType?.userPrice?.priceInPerson || 0
              : (transactionData?.clientFee || 0) / (transactionData?.appointment?.length || 1),
          ) || 'Not Specified',
        sideFields: [
          {
            label: 'Transaction Type',
            value: transactionType?.name,
            size: 12,
          },
        ],
      },
      ...transactionTextField,
      {
        label: 'Assigned To',
        value: (
          <div className='gx-d-flex gx-align-items-center'>
            {assigners.length > 1 ? (
              <Tooltip
                title={formatListNames(assigners.map((assigner) => `${assigner.firstName} ${assigner.lastName}`))}
                placement='top'>
                <div>
                  <AvatarGroup
                    sources={[...assigners]
                      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                      .map((assigner) => fastGetDefaultAvatarUrl(assigner))}
                  />
                </div>
              </Tooltip>
            ) : assigners.length === 1 ? (
              <div>
                {assigners[0].firstName} {assigners[0].lastName}
              </div>
            ) : (
              <div>None</div>
            )}
            {userRole === RoleEnum.CLIENT && (
              <div>
                <TextOnlyButtonStyled className='gx-ml-2' onClick={onAssignTransactionClick}>
                  Set Participants
                </TextOnlyButtonStyled>
              </div>
            )}
          </div>
        ),
        sideFields: [
          {
            label: 'Location',
            value: transactionData?.location?.name,
            size: 12,
          },
        ],
      },
    ];
  }, [transactionData, transactionType]);

  const defaultReturnCompletedDocumentsDataProps: DataFieldProps[] = useMemo(() => {
    const documentReturnAddress: Record<string, any> = transactionData?.returnShippingAddress || {};
    return [
      {
        label: 'Address',
        value: <AddressFormatComponent addressDetail={documentReturnAddress} separateLines />,
      },
    ];
  }, [transactionData]);

  const closeModal = () => {
    setModalType(undefined);
    setSelectedSignatory(undefined);
    setSelectedAppointment(undefined);
    setSelectedFile(undefined);
  };

  const mutationSuccess = (actionString?: string) => {
    defaultNotification({ success: actionString || 'Update transaction successfully!' });
    refetchTransactionData?.();
    callbackOnSuccess?.();
    closeModal();
  };

  const mutationFailure = (err: any) => {
    console.error('err', err);
    defaultNotification({ failure: 'Something went wrong! Please try again.' });
    callbackOnFailure?.();
  };

  // UPDATE TRANSACTION MUTATION
  const [updateTransaction, { loading: updateTransactionLoading }] = useMutation(UPDATE_TRANSACTION);
  const [updateAppointment, { loading: updateAppointmentLoading }] = useMutation(UPDATE_APPOINTMENT);
  const [createAppointment, { loading: createAppointmentLoading }] = useMutation(CREATE_APPOINTMENT);
  const [deleteAppointment, { loading: deleteAppointmentLoading }] = useMutation(DELETE_APPOINTMENT);

  const callUpdateTransactionMutation = async (newTransactionData: any, actionString?: string) => {
    try {
      await updateTransaction({
        variables: {
          updatedDoc: newTransactionData,
          filter: { id: transactionData?.id },
        },
      });
      mutationSuccess(actionString);
    } catch (err) {
      mutationFailure(err);
    }
  };

  const { uploadDocument, uploadPercentage, loading: uploadLabelLoading } = useUploadTransactionAppointmentFile({
    showPercentage: true,
  });

  const callCreateAppointmentMutation = async (appointmentData: any, actionString?: string) => {
    const { labelFiles, ...data } = appointmentData;
    try {
      const response = await createAppointment({
        variables: {
          doc: { ...data, transaction: transactionData?.id, id: undefined },
        },
      });
      const newAppointment: IAppointment = response.data.createAppointmentEntity;
      if (!data.autoGenShippingLabel) {
        uploadDocument({
          files: labelFiles,
          transactionId: transactionData?.id,
          appointmentIds: [newAppointment.id || ''],
          subType: DocumentSubType.SHIPPING_LABEL,
          callbacks: {
            success: () => mutationSuccess(actionString),
            fail: (err) => mutationFailure(err),
          },
        });
      } else {
        mutationSuccess(actionString);
      }
    } catch (err) {
      // do nothing
      mutationFailure(err);
    }
  };

  const callUpdateAppointmentMutation = async (appointmentData: any, actionString?: string) => {
    if (!selectedAppointment) return;
    const { labelFiles, ...data } = appointmentData;
    try {
      await updateAppointment({
        variables: {
          updatedDoc: data,
          filter: { id: selectedAppointment.id },
        },
      });
      if (!data.autoGenShippingLabel) {
        uploadDocument({
          files: labelFiles,
          transactionId: transactionData?.id,
          appointmentIds: [selectedAppointment.id || ''],
          subType: DocumentSubType.SHIPPING_LABEL,
          existedFiles: selectedAppointment.labelFiles,
          callbacks: {
            success: () => mutationSuccess(actionString),
            fail: (err) => mutationFailure(err),
          },
        });
      } else {
        mutationSuccess(actionString);
      }
    } catch (err) {
      // do nothing
      mutationFailure(err);
    }
  };

  const callDeleteAppointmentMutation = async (appointmentId: string, actionString?: string) => {
    if (!selectedAppointment) return;
    try {
      await deleteAppointment({
        variables: {
          id: appointmentId,
        },
      });
      mutationSuccess(actionString);
    } catch (err) {
      mutationFailure(err);
    }
  };

  // SIGNATORY MODAL ACTIONS

  const signatoriesDataConvertToArray = (sigData: Record<string, ISignatoryInputProps>) =>
    Object.entries(sigData || {}).reduce(
      (acc: { signatories: any[]; signatoriesId: string[] }, [sigId, sig]) => {
        acc.signatoriesId.push(sigId);
        acc.signatories.push(sig);
        return acc;
      },
      { signatories: [], signatoriesId: [] },
    ).signatories;

  const onAddSignatoryClick = () => {
    setModalType('addSignatory');
  };

  const onEditSignatoryClick = (sigData: ISignatoryInputProps) => {
    setModalType('editSignatory');
    setSelectedSignatory(sigData);
  };

  const onDeleteSignatoryClick = (sigData: ISignatoryInputProps) => {
    setModalType('deleteSignatory');
    setSelectedSignatory(sigData);
  };

  const onConfirmDeleteSignatory = () => {
    const newSignatoryData = { ...signatoriesData };
    delete newSignatoryData[selectedSignatory?.id || ''];
    const signatoriesInfo = signatoriesDataConvertToArray(newSignatoryData);
    callUpdateTransactionMutation({ signatories: signatoriesInfo });
  };

  const onSignatoryFormConfirm = (newValue: ISignatoryInputProps, id: string) => {
    const newSignatoryData = { ...signatoriesData, [id]: { ...newValue, id } };
    const signatoriesInfo = signatoriesDataConvertToArray(newSignatoryData);
    callUpdateTransactionMutation({ signatories: signatoriesInfo }, 'Signatory list updated!');
  };

  // APPOINTMENT ACTIONS

  const appointmentDataConvertToArray = (appointmentsData: Record<string, IAppointmentState>) =>
    Object.values(appointmentsData || {}).map((app) => {
      const {
        id,
        key,
        notary,
        assignments,
        clientInPersonFee,
        clientRONFee,
        notaryGetInPerson,
        notaryGetRON,
        status,
        ...restAppointment
      } = app;
      return (id || '').indexOf('new_') < 0 ? { ...restAppointment, id } : restAppointment;
    });

  const onAddAppointmentClick = () => {
    setModalType('addNotaryAppointment');
    // setNotaryAppointments([...notaryAppointments, undefined]);
  };

  const onEditAppointmentClick = (appointmentData: IAppointment, id: string) => {
    setModalType('editNotaryAppointment');
    setSelectedAppointment({ ...appointmentData, id });
  };

  const onDeleteAppointmentClick = (appointmentData: IAppointment, id: string) => {
    setModalType('deleteNotaryAppointment');
    setSelectedAppointment({ ...appointmentData, id });
  };

  const onAppointmentFormConfirm = (appointmentData: INotaryAppointmentInputProps, id: string) => {
    const [appointmentDate] = getFormattedDate(appointmentData.date);
    const convertedAppointmentDate = moment(appointmentDate).format(FORMAT_DATE);
    const otherAppointments: Record<string, IAppointmentState> = {};
    Object.entries(notaryAppointments || {}).forEach(([appId, appData]) => {
      otherAppointments[appId] = {
        id: appId,
        type: appData.type,
        signatoryIds: appData.signatoryIds,
        // duration: appData.duration,
        date: appData.date,
        address: appData.address,
        key: appId,
        fileIds: (appData.files || []).map((file) => file.id),
        description: appData.description,
        autoGenShippingLabel: appData.autoGenShippingLabel,
        labelFiles: appData.labelFiles,
      };
    });
    const newAppointmentsData = {
      // ...otherAppointments,
      [id]: {
        id,
        description: appointmentData.description,
        type: appointmentData.type,
        signatoryIds: appointmentData.signatoryIds,
        // duration: parseFloat(`${appointmentData.duration}`) * 3600,
        date: moment(`${convertedAppointmentDate} ${appointmentData.signedTime}`, FORMAT_DATE_TIME).toISOString(),
        address: {
          address1: appointmentData.address1,
          address2: appointmentData.address2,
          city: appointmentData.city,
          state: appointmentData.state,
          zip: appointmentData.zip,
          coordinate: [appointmentData.longtitude, appointmentData.latitude],
        },
        key: id,
        fileIds: appointmentData.fileIds || [],
        autoGenShippingLabel: appointmentData.autoGenShippingLabel,
        labelFiles: appointmentData.labelFiles,
        shippingOptions: appointmentData.shippingServiceType
          ? { serviceType: appointmentData.shippingServiceType }
          : undefined,
      },
    };

    if (selectedAppointment) {
      callUpdateAppointmentMutation(appointmentDataConvertToArray(newAppointmentsData)[0]);
    } else {
      callCreateAppointmentMutation(appointmentDataConvertToArray(newAppointmentsData)[0]);
    }
  };

  const onConfirmDeleteAppointment = () => {
    callDeleteAppointmentMutation(selectedAppointment?.id || '', 'Appointment deleted');
  };

  const isAppointmentEmpty = isEmpty(notaryAppointments);
  const isSignatoriesEmpty = isEmpty(signatoriesData);

  const deleteModalsName = useMemo(() => ['deleteSignatory', 'deleteNotaryAppointment'], []);
  const isDeleteModalShown = useMemo(() => !!modalType && deleteModalsName.includes(modalType), [modalType]);
  let onDeleteConfirm: GeneralFunction = onDeleteSignatoryClick;
  switch (modalType) {
    case 'deleteSignatory':
      onDeleteConfirm = onConfirmDeleteSignatory;
      break;
    case 'deleteNotaryAppointment':
      onDeleteConfirm = onConfirmDeleteAppointment;
      break;
    default:
      break;
  }

  const onAddNotaryClick = (appointmentData: IAppointment) => {
    setModalType('notary');
    setSelectedAppointment({ ...appointmentData });
  };

  // DOCUMENTS ACTIONS
  const onAddDocumentClick = () => {
    setModalType('addDocument');
  };

  const onEditFileClick = (document?: IDocument) => {
    setModalType('editDocument');
    setSelectedFile(document);
  };

  const [deleteFile] = useMutation(DELETE_FILE_SINGLE);
  const onDeleteFileClick = async (file?: IFile, callback?: GeneralFunction) => {
    try {
      await deleteFile({ variables: { id: file?.id || '' } });
      defaultNotification({ success: 'Delete file successfully!' });
      refetchTransactionData?.();
    } catch (err) {
      defaultNotification({ failure: 'Something went wrong! Please try again.' });
    }
    callback?.();
  };

  const showTransactionFormModal = () => {
    setModalType('transaction-form');
  };

  const onAssignTransactionSuccess = () => {
    refetchTransactionData?.();
  };

  // return document location action
  const onChangeReturnLocationButtonClick = () => {
    setModalType('changeLocation');
  };

  return (
    <>
      <NotaryTableModal
        visible={modalType === 'notary'}
        closeModal={closeModal}
        selectedAppointment={selectedAppointment}
        refetchTransactionData={refetchTransactionData}
      />
      <DefaultModal
        props={{
          width: 800,
          style: { maxWidth: '100%', padding: 0 },
          visible: modalType === 'assign-transaction',
          title: 'Assign Transaction',
          onCancel: closeModal,
        }}>
        <ClientAssignTable transaction={transactionData} callbackOnAssignSuccess={onAssignTransactionSuccess} />
      </DefaultModal>
      <TransactionFormContainerStyled className='transaction-form-container'>
        <SweetAlert
          show={isDeleteModalShown}
          error
          title={formatMessage(intl, 'appModule.deleteRecordTitle')}
          confirmBtnText={formatMessage(intl, 'appModule.delete')}
          onConfirm={onDeleteConfirm}
          onCancel={closeModal}
          showCancel
          reverseButtons>
          <IntlMessages id='appModule.deleteRecordBody' />
        </SweetAlert>
        <TransactionSectionContainerStyled className='gx-form-row0 gx-w-100'>
          <SectionHeader
            buttonTitle={isTransactionEditable && isClientRelatedToTransaction ? 'Edit Transaction' : undefined}
            onHeaderButtonClick={showTransactionFormModal}>
            Transaction
          </SectionHeader>
          <TransactionModal
            closeForm={closeModal}
            visible={modalType === 'transaction-form'}
            selectedTransactionType={transactionType}
            transactionTypes={transactionTypesData}
            transaction={transactionData}
            locations={userPermittedLocationsData}
            fetchNextPageLocationData={fetchNextPageLocationData}
          />
          <InputsContainerStyled className='fields-container transaction-detail-content'>
            <Row className='gx-m-0 gx-p-0'>
              <Col span={24} className='gx-m-0 gx-p-0'>
                <InputFieldsGenerator dataFields={transactionFormField} />
              </Col>
            </Row>
          </InputsContainerStyled>
        </TransactionSectionContainerStyled>
        <TransactionSectionContainerStyled>
          <SignatoryModal
            modalType={isClientRelatedToTransaction ? modalType : undefined}
            closeModal={closeModal}
            addSignatory={onSignatoryFormConfirm}
            editSignatory={onSignatoryFormConfirm}
            data={selectedSignatory}
            isLoading={updateTransactionLoading}
          />
          <SectionHeader
            separatorPosition='top'
            buttonTitle={isClientRelatedToTransaction ? '+ Signatory' : undefined}
            onHeaderButtonClick={onAddSignatoryClick}>
            Signatories
          </SectionHeader>
          {isSignatoriesEmpty && (
            <Row className='gx-mx-0 max-w-3xl transaction-detail-content'>
              <Col span={24}>
                <p style={{ fontSize: 16 }}>No Signatories Created</p>
                <p>
                  A signatory is any person who will be signing transaction documents. You can create one or more
                  signatories for this transaction. At least one signatory is required.
                </p>
              </Col>
            </Row>
          )}
          <Row className='gx-mx-0'>
            {Object.entries(signatoriesData || {}).map(([sigId, sigData]) => {
              const isSigUsedInAppointment = transactionData?.appointment?.some((app) =>
                app.signatoryIds.includes(sigId),
              );
              return (
                <SignatoryCard
                  onEditClick={onEditSignatoryClick}
                  onDeleteClick={isSigUsedInAppointment ? undefined : onDeleteSignatoryClick}
                  key={sigId}
                  signatoryData={sigData}
                />
              );
            })}
          </Row>
        </TransactionSectionContainerStyled>
        <TransactionSectionContainerStyled>
          <AppointmentModal
            transactionTypeConfigurations={transactionData?.transactionType?.configs}
            isLoading={updateAppointmentLoading || createAppointmentLoading || uploadLabelLoading}
            modalType={modalType}
            closeModal={closeModal}
            addAppointment={onAppointmentFormConfirm}
            editAppointment={onAppointmentFormConfirm}
            data={selectedAppointment}
            signatoriesData={signatoriesData}
            transaction={transactionData}
            filePercentageUpload={uploadPercentage}
            fileUploadLoading={uploadLabelLoading}
          />
          <SectionHeader
            separatorPosition='top'
            buttonTitle={isClientRelatedToTransaction ? '+ Appointment' : undefined}
            onHeaderButtonClick={onAddAppointmentClick}>
            Appointments
          </SectionHeader>
          {isAppointmentEmpty && (
            <Row className='gx-mx-0 max-w-3xl transaction-detail-content'>
              <Col span={24}>
                <p style={{ fontSize: 16 }}>No Notary Appointments Created</p>
                <p>
                  Notary appointments are created for one or more signatories per appointment. More than one notary
                  appointment can be created for this transaction. Each appointment can be linked to the specific
                  transaction documents that need to be signed by the selected signatories.
                </p>
              </Col>
            </Row>
          )}
          {/* Appointment form */}
          {/* <InputFieldsGenerator formFields={notaryAppointmentFormField} /> */}
          <AppointmentList
            onAddNotaryClick={onAddNotaryClick}
            onEditClick={isClientRelatedToTransaction ? onEditAppointmentClick : undefined}
            onDeleteClick={isClientRelatedToTransaction ? onDeleteAppointmentClick : undefined}
            data={notaryAppointments}
            signatoriesData={signatoriesData}
            // shippingLabelDocument={transactionShippingDocument}
          />
        </TransactionSectionContainerStyled>
        {isClientRelatedToTransaction && (
          <TransactionSectionContainerStyled>
            <DocumentModal
              modalType={modalType}
              closeModal={closeModal}
              appointments={transactionData?.appointment}
              file={selectedFile}
              transaction={transactionData}
              refetchTransactionData={refetchTransactionData}
            />
            <SectionHeader
              separatorPosition='top'
              buttonTitle={isClientRelatedToTransaction ? '+ Document' : undefined}
              onHeaderButtonClick={onAddDocumentClick}>
              Documents
            </SectionHeader>

            <div className='transaction-detail-content'>
              <DocumentList
                onEditFileClick={onEditFileClick}
                onDeleteFileClick={onDeleteFileClick}
                files={transactionAppointmentDocument?.files}
              />
            </div>
          </TransactionSectionContainerStyled>
        )}
        <TransactionSectionContainerStyled>
          <DocumentModal
            modalType={modalType}
            closeModal={closeModal}
            appointments={transactionData?.appointment}
            file={selectedFile}
            transaction={transactionData}
            refetchTransactionData={refetchTransactionData}
          />
          <SectionHeader
            separatorPosition='top'
            buttonTitle={isClientRelatedToTransaction ? 'Change Location' : undefined}
            onHeaderButtonClick={onChangeReturnLocationButtonClick}>
            Completed Document Return Address
          </SectionHeader>
          <DocumentReturnLocationModal
            transaction={transactionData}
            visible={modalType === 'changeLocation'}
            closeModal={closeModal}
            refetchTransactionData={refetchTransactionData}
          />
          <InputsContainerStyled className='fields-container transaction-detail-content px-4'>
            <InputFieldsGenerator dataFields={defaultReturnCompletedDocumentsDataProps} />
          </InputsContainerStyled>
        </TransactionSectionContainerStyled>
      </TransactionFormContainerStyled>
    </>
  );
};

export default TransactionForm;
